import type { RouteComponentProps } from "@reach/router";

import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import { PROFILE_TYPE, getTabLabel } from "../types";
import SectorPill from "newui/components/PillDisplay/SectorPill";
import DisciplinePill from "newui/components/PillDisplay/DisciplinePill";
import PillDisplay, { PillDisplayType } from "newui/components/PillDisplay/PillDisplay";

type SectorsSpecialtyTabProps = RouteComponentProps;

interface TagItem {
	id: number;
	name: string;
}

const PillGroup: React.FC<{ items: TagItem[]; label: string; pillType?: PillDisplayType, }> = ({
	label,
	items,
	pillType = "discipline",
}) => {
	if (!items || items.length === 0) return null;

	return (
		<div className="space-y-2">
			<h3 className="text-sm font-semibold text-gray-700">{label}</h3>
			<div className="flex flex-wrap gap-2">
				{items.map((item) => (
					<PillDisplay name={item.name} key={`${pillType}${item.id}`} type={pillType} />
				))}
			</div>
		</div>
	);
};

export const SectorsSpecialtyTab: React.FC<SectorsSpecialtyTabProps> = () => {
	const { company, profileTypeId } = useCompanyPreviewData();

	if (!company) return null;

	const sectors = company.sector ?? [];
	const specialities = company.speciality ?? [];
	const trades = company.trade ?? [];
	const disciplines = company.disciplines ?? [];
	const disciplinesFormatted = company.disciplinesFormatted ?? [];

	const getTypeData = (): TagItem[] | null => {
		switch (profileTypeId) {
			case PROFILE_TYPE.SPECIALTY:
				return specialities;
			case PROFILE_TYPE.TRADE:
				return trades;
			case PROFILE_TYPE.DISCIPLINES:
				return disciplines;
			default:
				return null;
		}
	};

	const typeLabel = profileTypeId ? getTabLabel(profileTypeId) : null;
	const typeData = getTypeData();

	return (
		<div className="space-y-6">
			{/* Banner */}
			{company.banner_path && (
				<img
					src={company.banner_path}
					alt={`${company.name} banner`}
					className="h-48 w-full rounded-lg object-cover sm:h-56 md:h-64"
				/>
			)}

			{/* Sectors */}
			<PillGroup label="Relevant Sectors & Experience" items={sectors} pillType="sector" />

			{/* Disciplines grouped by expertise (profile type 4) */}
			{profileTypeId === PROFILE_TYPE.DISCIPLINES &&
				disciplinesFormatted.length > 0 &&
				disciplinesFormatted.map(
					(
						discipline: { disciplines: number[]; expertise: string; },
						index: number
					) => {
						const matchedDisciplines = disciplines.filter((d: TagItem) =>
							discipline.disciplines?.includes(d.id)
						);
						return (
							<div key={index} className="space-y-2">
								<h3 className="text-sm font-semibold text-gray-700">
									Expertise: {discipline.expertise}
								</h3>
								<PillGroup
									label="Disciplines"
									items={matchedDisciplines}
								/>
							</div>
						);
					}
				)}

			{/* Specialty for Trade profile type */}
			{profileTypeId === PROFILE_TYPE.TRADE && (
				<PillGroup label="Specialty" items={specialities} />
			)}

			{/* Generic type data for non-Disciplines types */}
			{typeData &&
				typeLabel &&
				profileTypeId !== PROFILE_TYPE.DISCIPLINES && (
					<PillGroup label={typeLabel} items={typeData} />
				)}
		</div>
	);
};
